import {
  Component,
  computed,
  effect,
  ElementRef,
  EventEmitter,
  inject,
  input,
  Output,
  signal,
  ViewChild,
} from '@angular/core';
import { DecimalPipe } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { DevBottomBarService } from '../dev-bottom-bar.service';

// ── Types ───────────────────────────────────────────────────────────────────

type ViewType = 'json' | 'tree' | 'graph';
type NodeKind = 'open' | 'close' | 'collapsed' | 'leaf' | 'insert';
type JsonType = 'object' | 'array' | 'string' | 'number' | 'boolean' | 'null';

interface GraphProp {
  key: string;
  value: string;
  type: JsonType;
  path: (string | number)[];
}

interface GraphNode {
  id: string;
  key: string;
  path: (string | number)[];
  type: 'object' | 'array';
  props: GraphProp[];    // primitive properties shown as table rows
  x: number;
  y: number;
  children: string[];   // ids of child container nodes
  parent: string | null;
  collapsed: boolean;
  width: number;
  height: number;
}

interface GraphLink {
  from: string;
  to: string;
}

interface FlatNode {
  id: string;
  kind: NodeKind;
  path: (string | number)[];
  key: string;
  depth: number;
  type: JsonType;
  childCount: number;
  displayValue: string;
  hasComma: boolean;
  insertIndex?: number;
}

// ── Component ───────────────────────────────────────────────────────────────

@Component({
  selector: 'app-json-viewer',
  standalone: true,
  imports: [FormsModule, MatIconModule, DecimalPipe],
  host: {
    '[class.jv-maximized]': 'maximized()',
    '[style.height.px]': 'maximized() ? barService.heightPx$.value : null',
  },
  template: `
    <div class="jv">
      <div class="jv-toolbar">
        <button class="jv-tab" [class.active]="activeView() === 'json'" (click)="setView('json')">
          <mat-icon>data_object</mat-icon> JSON
        </button>
        <button class="jv-tab" [class.active]="activeView() === 'tree'" (click)="setView('tree')">
          <mat-icon>account_tree</mat-icon> Tree
        </button>
        <button class="jv-tab" [class.active]="activeView() === 'graph'" (click)="setView('graph')">
          <mat-icon>hub</mat-icon> Graph
        </button>
        <span class="jv-spacer"></span>
        @if (activeView() === 'json') {
          <button class="jv-tool" (click)="toggleWrap()" [title]="wordWrap() ? 'Disable word wrap' : 'Enable word wrap'">
            <mat-icon>{{ wordWrap() ? 'wrap_text' : 'arrow_right_alt' }}</mat-icon>
          </button>
          <button class="jv-tool" (click)="toggleMinify()" [title]="minified() ? 'Pretty print' : 'Minify'">
            <mat-icon>{{ minified() ? 'format_indent_increase' : 'compress' }}</mat-icon>
          </button>
        }
        @if (activeView() === 'tree') {
          <button class="jv-tool" (click)="expandAll()" title="Expand all">
            <mat-icon>unfold_more</mat-icon>
          </button>
          <button class="jv-tool" (click)="collapseAll()" title="Collapse all">
            <mat-icon>unfold_less</mat-icon>
          </button>
        }
        @if (activeView() === 'graph') {
          <button class="jv-tool" (click)="graphFocusRoot()" title="Focus root">
            <mat-icon>crop_free</mat-icon>
          </button>
          <button class="jv-tool" (click)="graphZoomIn()" title="Zoom in">
            <mat-icon>zoom_in</mat-icon>
          </button>
          <button class="jv-tool" (click)="graphZoomOut()" title="Zoom out">
            <mat-icon>zoom_out</mat-icon>
          </button>
          <button class="jv-tool" (click)="graphFit()" title="Fit to view">
            <mat-icon>fit_screen</mat-icon>
          </button>
          <button class="jv-tool" (click)="graphExpandAll()" title="Expand all">
            <mat-icon>unfold_more</mat-icon>
          </button>
          <button class="jv-tool" (click)="graphCollapseAll()" title="Collapse all">
            <mat-icon>unfold_less</mat-icon>
          </button>
          <button class="jv-tool" (click)="graphSmartCollapse()" title="Smart collapse">
            <mat-icon>auto_awesome</mat-icon>
          </button>
        }
        <button class="jv-tool" (click)="toggleSearch()" title="Search (Ctrl+F)"
                [class.jv-tool-active]="searchOpen()">
          <mat-icon>search</mat-icon>
        </button>
        <button class="jv-tool" (click)="downloadJson()" title="Download as JSON">
          <mat-icon>download</mat-icon>
        </button>
        <button class="jv-tool" (click)="toggleMaximize()" [title]="maximized() ? 'Restore' : 'Maximize'">
          <mat-icon>{{ maximized() ? 'close_fullscreen' : 'open_in_full' }}</mat-icon>
        </button>
        @if (mode() === 'edit') {
          <button class="jv-tool jv-save" (click)="onSave()" title="Save changes">
            <mat-icon>save</mat-icon>
          </button>
        }
      </div>

      @if (searchOpen()) {
        <div class="jv-search-bar">
          <mat-icon>search</mat-icon>
          <input class="jv-search-input" type="text" placeholder="Search…"
                 [value]="searchText()"
                 (input)="onSearchInput($any($event.target).value)"
                 (keydown.enter)="nextMatch()"
                 (keydown.escape)="toggleSearch()" />
          @if (searchText()) {
            <span class="jv-search-count">{{ activeView() === 'graph' ? (graphSearchMatchList().length > 0 ? (currentMatchIdx() + 1) + '/' + graphSearchMatchList().length : '0') : searchMatchCount() }}</span>
          }
          <button class="jv-search-nav" (click)="prevMatch()" title="Previous">
            <mat-icon>expand_less</mat-icon>
          </button>
          <button class="jv-search-nav" (click)="nextMatch()" title="Next">
            <mat-icon>expand_more</mat-icon>
          </button>
          <button class="jv-search-nav" (click)="toggleSearch()" title="Close">
            <mat-icon>close</mat-icon>
          </button>
        </div>
      }

      <div class="jv-content" #contentEl
           [class.jv-content-graph]="activeView() === 'graph'"
           [style.height.px]="maximized() ? null : currentHeight()">
        @if (activeView() === 'json') {
          @if (hasCircular()) {
            <div class="jv-circular-warning">
              <mat-icon>warning</mat-icon>
              <span>Este JSON contiene una referencia circular. Elige un modo de visualización diferente a "Texto". (Por ejemplo, "Tree" o "Graph")</span>
            </div>
          } @else if (mode() === 'edit') {
            <textarea class="jv-text edit"
                      [class.no-wrap]="!wordWrap()"
                      [(ngModel)]="jsonEditText"
                      spellcheck="false"
                      placeholder="Enter JSON…"></textarea>
          } @else {
            <pre class="jv-text view" [class.no-wrap]="!wordWrap()" [innerHTML]="highlightedJsonWithSearch()"></pre>
          }
        } @else if (activeView() === 'tree') {
          <div class="jv-tree">
            @for (node of visibleNodes(); track node.id) {
              @if (node.kind === 'insert') {
                <div class="tree-insert-zone" [style.padding-left.px]="node.depth * 16 + 6"
                     (click)="insertAtNode(node)">
                  <div class="insert-line">
                    <mat-icon class="insert-icon">add</mat-icon>
                  </div>
                </div>
              } @else {
              <div class="tree-row" [class.search-match]="isSearchMatch(node)"
                   [style.padding-left.px]="node.depth * 16 + 6"
                   (mouseenter)="hoveredPath.set(node.path)"
                   (mouseleave)="hoveredPath.set(null)">
                @switch (node.kind) {

                  @case ('open') {
                    <span class="tg" (click)="toggleNode(node)">&#x25BE;</span>
                    @if (node.key) {
                      @if (isEditingNode(node, 'key')) {
                        <input class="inline-edit key-edit"
                               [value]="editingText()"
                               (input)="editingText.set($any($event.target).value)"
                               (keydown.enter)="commitKeyEdit(node)"
                               (keydown.escape)="cancelEdit()"
                               (blur)="commitKeyEdit(node)" />
                      } @else {
                        <span class="tk" (dblclick)="startEdit(node, 'key')">{{ node.key }}</span>
                      }
                      <span class="tc">:&nbsp;</span>
                    }
                    <span class="tb">{{ node.type === 'object' ? '{' : '[' }}</span>
                  }

                  @case ('close') {
                    <span class="tg-space"></span>
                    <span class="tb">{{ node.type === 'object' ? '}' : ']' }}</span>
                    @if (node.hasComma) { <span class="comma">,</span> }
                  }

                  @case ('collapsed') {
                    <span class="tg" (click)="toggleNode(node)">&#x25B8;</span>
                    @if (node.key) {
                      @if (isEditingNode(node, 'key')) {
                        <input class="inline-edit key-edit"
                               [value]="editingText()"
                               (input)="editingText.set($any($event.target).value)"
                               (keydown.enter)="commitKeyEdit(node)"
                               (keydown.escape)="cancelEdit()"
                               (blur)="commitKeyEdit(node)" />
                      } @else {
                        <span class="tk" (dblclick)="startEdit(node, 'key')">{{ node.key }}</span>
                      }
                      <span class="tc">:&nbsp;</span>
                    }
                    <span class="tb">{{ node.type === 'object' ? '{' : '[' }}</span>
                    <span class="child-count">{{ node.childCount }}</span>
                    <span class="tb">{{ node.type === 'object' ? '}' : ']' }}</span>
                    @if (node.hasComma) { <span class="comma">,</span> }
                  }

                  @case ('leaf') {
                    <span class="tg-space"></span>
                    @if (node.key) {
                      @if (isEditingNode(node, 'key')) {
                        <input class="inline-edit key-edit"
                               [value]="editingText()"
                               (input)="editingText.set($any($event.target).value)"
                               (keydown.enter)="commitKeyEdit(node)"
                               (keydown.escape)="cancelEdit()"
                               (blur)="commitKeyEdit(node)" />
                      } @else {
                        <span class="tk" (dblclick)="startEdit(node, 'key')">{{ node.key }}</span>
                      }
                      <span class="tc">:&nbsp;</span>
                    }
                    @if (isEditingNode(node, 'value')) {
                      <input class="inline-edit value-edit"
                             [value]="editingText()"
                             (input)="editingText.set($any($event.target).value)"
                             (keydown.enter)="commitValueEdit(node)"
                             (keydown.escape)="cancelEdit()"
                             (blur)="commitValueEdit(node)" />
                    } @else {
                      <span class="tv" [class]="'t-' + node.type"
                            (dblclick)="startEdit(node, 'value')">{{ node.displayValue }}</span>
                    }
                    @if (node.hasComma) { <span class="comma">,</span> }
                  }
                }
              </div>
              }
            }
            @if (visibleNodes().length === 0) {
              <span class="tree-empty">(empty)</span>
            }
          </div>
          @if (hoveredPath()) {
            <div class="path-breadcrumb">
              <span class="breadcrumb-text">{{ formatPath(hoveredPath()!) }}</span>
              <button class="breadcrumb-btn" (click)="copyPath(hoveredPath()!)" title="Copy path">path</button>
              <button class="breadcrumb-btn" (click)="copyAccessor(hoveredPath()!)" title="Copy accessor">accessor</button>
              <button class="breadcrumb-btn" (click)="copyValueAt(hoveredPath()!)" title="Copy value">value</button>
            </div>
          }
        }

        @if (activeView() === 'graph') {
          <div class="graph-container"
               (mousedown)="onGraphMouseDown($event)"
               (mousemove)="onGraphMouseMove($event)"
               (mouseup)="onGraphMouseUp($event)"
               (mouseleave)="onGraphMouseUp($event)"
               (wheel)="onGraphWheel($event)"
               (dblclick)="onGraphDblClick($event)"
               (dragstart)="$event.preventDefault()">
            <svg class="graph-svg">
              <g [attr.transform]="graphTransform()">
              <!-- Links -->
              @for (link of graphLinks(); track link.from + link.to) {
                <path class="graph-link" [attr.d]="getGraphLinkPath(link)" />
              }
              <!-- Nodes -->
              @for (node of graphVisibleNodes(); track node.id) {
                <g class="graph-node"
                   [class.graph-node-object]="node.type === 'object'"
                   [class.graph-node-array]="node.type === 'array'"
                   [class.graph-node-focused]="graphFocusedId() === node.id"
                   [class.graph-node-search-match]="isGraphSearchMatch(node)"
                   [attr.transform]="'translate(' + node.x + ',' + node.y + ')'"
                   (click)="onGraphNodeClick(node, $event)">
                  <!-- Background -->
                  <rect class="graph-node-bg"
                        x="0" y="0"
                        [attr.width]="node.width" [attr.height]="node.height"
                        rx="6" ry="6" />
                  <!-- Header bar -->
                  <rect class="graph-node-header"
                        x="0" y="0"
                        [attr.width]="node.width" height="24"
                        rx="6" ry="6" />
                  <rect class="graph-node-header-fill"
                        x="0" y="12"
                        [attr.width]="node.width" height="12" />
                  <!-- Header text -->
                  <text class="graph-node-icon" x="8" y="16">
                    {{ node.type === 'object' ? '{}' : '[]' }}
                  </text>
                  @if (graphEditingId() === node.id && graphEditingPropIdx() === null && graphEditingField() === 'key') {
                    <foreignObject x="22" y="1" [attr.width]="node.width - 50" height="22">
                      <input xmlns="http://www.w3.org/1999/xhtml" class="graph-inline-input"
                             [value]="graphEditText()"
                             (input)="graphEditText.set($any($event.target).value)"
                             (keydown.enter)="commitGraphKeyEdit(node)"
                             (keydown.escape)="cancelGraphEdit()"
                             (blur)="commitGraphKeyEdit(node)"
                             (click)="$event.stopPropagation()"
                             (mousedown)="$event.stopPropagation()"
                             (mouseup)="$event.stopPropagation()" />
                    </foreignObject>
                  } @else {
                    <text class="graph-node-title" x="24" y="16">
                      {{ node.key }}
                    </text>
                    @if (mode() === 'edit' && node.path.length > 0 && typeof node.path[node.path.length - 1] === 'string') {
                      <g class="graph-edit-btn graph-edit-icon-header"
                         (click)="startEditNodeKey(node, $event)"
                         (mousedown)="$event.stopPropagation()"
                         (mouseup)="$event.stopPropagation()">
                        <circle cx="24" cy="12" r="7" class="graph-edit-btn-bg" />
                        <text class="graph-edit-btn-icon" x="24" y="12">✎</text>
                      </g>
                    }
                  }
                  <text class="graph-node-badge" [attr.x]="node.width - 8" y="16">
                    {{ getOriginalPropCount(node) + node.children.length }}
                  </text>
                  <!-- Props toggle (left side of header) -->
                  @if (hasPropsForNode(node)) {
                    <circle class="graph-toggle graph-toggle-props" cx="0" cy="12"
                            r="7" (click)="toggleGraphProps(node, $event)" />
                    <text class="graph-toggle-text" x="0" y="15.5"
                          text-anchor="middle" (click)="toggleGraphProps(node, $event)">
                      {{ isPropsCollapsed(node) ? '…' : '−' }}
                    </text>
                  }
                  <!-- Separator line -->
                  <line class="graph-node-sep" x1="0" y1="24" [attr.x2]="node.width" y2="24" />
                  <!-- Property rows -->
                  @for (prop of node.props; track prop.key; let i = $index) {
                    <!-- Key cell -->
                    @if (graphEditingId() === node.id && graphEditingPropIdx() === i && graphEditingField() === 'key') {
                      <foreignObject x="4" [attr.y]="26 + i * 16" [attr.width]="node.width / 2 - 6" height="18">
                        <input xmlns="http://www.w3.org/1999/xhtml" class="graph-inline-input graph-inline-prop"
                               [value]="graphEditText()"
                               (input)="graphEditText.set($any($event.target).value)"
                               (keydown.enter)="commitPropKeyEdit(node, prop)"
                               (keydown.escape)="cancelGraphEdit()"
                               (blur)="commitPropKeyEdit(node, prop)"
                               (focus)="$any($event.target).select()"
                               (click)="$event.stopPropagation()"
                               (mousedown)="$event.stopPropagation()"
                               (mouseup)="$event.stopPropagation()"
                               autofocus />
                      </foreignObject>
                    } @else {
                      <g class="graph-prop-cell">
                        <rect class="graph-prop-hit" x="2" [attr.y]="26 + i * 16"
                              [attr.width]="node.width / 2 - 4" height="16" />
                        @if (mode() === 'edit' && node.type !== 'array') {
                          <g class="graph-edit-btn"
                             (click)="startEditPropKey(node, prop, i, $event)"
                             (mousedown)="$event.stopPropagation()"
                             (mouseup)="$event.stopPropagation()">
                            <circle cx="6" [attr.cy]="33 + i * 16" r="6" class="graph-edit-btn-bg" />
                            <text class="graph-edit-btn-icon" x="6" [attr.y]="33 + i * 16">✎</text>
                          </g>
                        }
                        <text class="graph-prop-key" x="8" [attr.y]="38 + i * 16">
                          {{ prop.key }}
                        </text>
                      </g>
                    }
                    <!-- Value cell -->
                    @if (graphEditingId() === node.id && graphEditingPropIdx() === i && graphEditingField() === 'value') {
                      <foreignObject [attr.x]="node.width / 2" [attr.y]="26 + i * 16" [attr.width]="node.width / 2 - 4" height="18">
                        <input xmlns="http://www.w3.org/1999/xhtml" class="graph-inline-input graph-inline-prop"
                               [value]="graphEditText()"
                               (input)="graphEditText.set($any($event.target).value)"
                               (keydown.enter)="commitPropValueEdit(node, prop)"
                               (keydown.escape)="cancelGraphEdit()"
                               (blur)="commitPropValueEdit(node, prop)"
                               (focus)="$any($event.target).select()"
                               (click)="$event.stopPropagation()"
                               (mousedown)="$event.stopPropagation()"
                               (mouseup)="$event.stopPropagation()"
                               autofocus />
                      </foreignObject>
                    } @else {
                      <g class="graph-prop-cell">
                        <rect class="graph-prop-hit" [attr.x]="node.width / 2" [attr.y]="26 + i * 16"
                              [attr.width]="node.width / 2 - 2" height="16" />
                        @if (mode() === 'edit') {
                          <g class="graph-edit-btn"
                             (click)="startEditPropValue(node, prop, i, $event)"
                             (mousedown)="$event.stopPropagation()"
                             (mouseup)="$event.stopPropagation()">
                            <circle [attr.cx]="node.width / 2 + 6" [attr.cy]="33 + i * 16" r="6" class="graph-edit-btn-bg" />
                            <text class="graph-edit-btn-icon" [attr.x]="node.width / 2 + 6" [attr.y]="33 + i * 16">✎</text>
                          </g>
                        }
                        <text [attr.class]="'graph-prop-val gv-' + prop.type"
                              [attr.x]="node.width - 8" [attr.y]="38 + i * 16">
                          {{ prop.value }}
                        </text>
                      </g>
                    }
                  }
                  <!-- Children collapse/expand toggle (right side of header) -->
                  @if (node.children.length > 0) {
                    <circle class="graph-toggle" [attr.cx]="node.width" [attr.cy]="12"
                            r="7" (click)="toggleGraphNode(node, $event)" />
                    <text class="graph-toggle-text" [attr.x]="node.width" y="15.5"
                          text-anchor="middle" (click)="toggleGraphNode(node, $event)">
                      {{ node.collapsed ? '+' : '−' }}
                    </text>
                  }
                  <!-- Add child button in edit mode -->
                  @if (mode() === 'edit' && !node.collapsed) {
                    <circle class="graph-add-btn" [attr.cx]="node.width / 2" [attr.cy]="node.height + 4"
                            r="7" (click)="addGraphChild(node, $event)" />
                    <text class="graph-add-text" [attr.x]="node.width / 2" [attr.y]="node.height + 7.5"
                          text-anchor="middle" (click)="addGraphChild(node, $event)">+</text>
                  }
                </g>
              }
              </g>
            </svg>
            <div class="graph-info">
              <span>{{ graphVisibleNodes().length }} nodes</span>
              <span>{{ (graphScale() * 100) | number:'1.0-0' }}%</span>
            </div>
          </div>
        }
      </div>

      @if (parseError()) {
        <div class="jv-error">{{ parseError() }}</div>
      }

      @if (validationWarnings().length > 0) {
        <div class="jv-warnings">
          <div class="jv-warn-header">
            <mat-icon>warning</mat-icon>
            <span>Schema warnings ({{ validationWarnings().length }})</span>
          </div>
          @for (w of validationWarnings(); track w.path + w.message) {
            <div class="jv-warn-item">
              <span class="jv-warn-path">{{ w.path }}</span>
              <span class="jv-warn-msg">{{ w.message }}</span>
            </div>
          }
        </div>
      }

      @if (resizable() || maximized()) {
        <div class="jv-resize" (mousedown)="startResize($event)"></div>
      }
    </div>
  `,
  styles: [`
    :host {
      display: block;
    }
    :host.jv-maximized {
      position: fixed;
      bottom: 0;
      left: 0;
      right: 0;
      z-index: 10001;
      display: flex;
      background: #14142b;
    }
    @media (min-width: 960px) {
      :host.jv-maximized { left: 280px; }
    }
    :host.jv-maximized .jv {
      position: relative;
      width: 100%;
      height: 100%;
      border-radius: 0;
      border: none;
      display: flex;
      flex-direction: column;
      overflow: hidden;
    }
    :host.jv-maximized .jv-toolbar {
      flex-shrink: 0;
    }
    :host.jv-maximized .jv-search-bar {
      flex-shrink: 0;
    }
    :host.jv-maximized .jv-content {
      flex: 1;
      overflow: auto;
      min-height: 0;
    }
    :host.jv-maximized .jv-resize {
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      z-index: 2;
      background: transparent;
    }

    .jv {
      border: 1px solid #23233a;
      border-radius: 3px;
      overflow: hidden;
      font-family: ui-monospace, monospace;
      font-size: 0.72rem;
    }

    /* ── Toolbar ──────────────────────────────────────────────── */

    .jv-toolbar {
      display: flex;
      align-items: center;
      gap: 2px;
      padding: 2px 4px;
      background: #0f0f1e;
      border-bottom: 1px solid #23233a;
    }
    .jv-tab {
      display: inline-flex;
      align-items: center;
      gap: 3px;
      background: none;
      border: 1px solid transparent;
      border-radius: 3px;
      color: #6b7280;
      font-family: inherit;
      font-size: 0.66rem;
      font-weight: 600;
      padding: 2px 6px;
      cursor: pointer;
      transition: all 0.12s;
    }
    .jv-tab mat-icon {
      font-size: 12px; width: 12px; height: 12px; line-height: 12px;
    }
    .jv-tab:hover { color: #d4d4d8; }
    .jv-tab.active {
      color: #e5e7eb;
      background: #1e1e3a;
      border-color: #3a3a5a;
    }
    .jv-spacer { flex: 1; }
    .jv-tool {
      background: none;
      border: none;
      color: #6b7280;
      padding: 2px;
      cursor: pointer;
      display: inline-flex;
      align-items: center;
      border-radius: 3px;
    }
    .jv-tool mat-icon {
      font-size: 14px; width: 14px; height: 14px; line-height: 14px;
    }
    .jv-tool:hover { color: #e5e7eb; background: #374151; }
    .jv-save { color: #3b82f6; }
    .jv-save:hover { color: #60a5fa; background: rgba(59,130,246,0.15); }

    /* ── Content ──────────────────────────────────────────────── */

    .jv-content {
      overflow: auto;
      background: #14142b;
      min-height: 40px;
      position: relative;
    }
    .jv-content.jv-content-graph {
      display: flex;
      flex-direction: column;
      overflow: hidden;
    }

    /* JSON text (view + edit) */
    .jv-text {
      margin: 0;
      padding: 6px 8px;
      color: #e5e7eb;
      font-family: ui-monospace, monospace;
      font-size: 0.7rem;
      white-space: pre-wrap;
      word-break: break-word;
      line-height: 1.45;
    }
    .jv-text.no-wrap {
      white-space: pre;
      word-break: normal;
      overflow-x: auto;
    }

    /* Syntax highlighting */
    .jv-text :deep(.hl-key)     { color: #93c5fd; }
    .jv-text :deep(.hl-string)  { color: #86efac; }
    .jv-text :deep(.hl-number)  { color: #fde68a; }
    .jv-text :deep(.hl-boolean) { color: #c4b5fd; }
    .jv-text :deep(.hl-null)    { color: #6b7280; font-style: italic; }
    .jv-text.view {
      height: 100%;
      box-sizing: border-box;
      overflow: auto;
    }
    .jv-text.edit {
      display: block;
      width: 100%;
      height: 100%;
      background: #14142b;
      border: none;
      outline: none;
      resize: none;
      box-sizing: border-box;
      color: #e5e7eb;
    }

    .jv-circular-warning {
      display: flex;
      align-items: center;
      gap: 10px;
      padding: 16px 20px;
      margin: 12px;
      background: #2d1b00;
      border: 1px solid #b45309;
      border-radius: 6px;
      color: #fbbf24;
      font-size: 0.8rem;
      line-height: 1.4;
    }
    .jv-circular-warning mat-icon {
      color: #f59e0b;
      font-size: 20px;
      width: 20px;
      height: 20px;
      flex-shrink: 0;
    }

    /* ── Tree ─────────────────────────────────────────────────── */

    .jv-tree {
      padding: 4px 0;
    }

    .tree-row {
      display: flex;
      align-items: baseline;
      min-height: 20px;
      line-height: 20px;
      white-space: nowrap;
    }
    .tree-row:hover {
      background: rgba(59, 130, 246, 0.06);
    }

    /* Toggle arrow */
    .tg {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      width: 14px;
      flex-shrink: 0;
      cursor: pointer;
      color: #64748b;
      font-size: 10px;
      user-select: none;
    }
    .tg:hover { color: #e5e7eb; }
    .tg-space {
      display: inline-block;
      width: 14px;
      flex-shrink: 0;
    }

    /* Key */
    .tk {
      color: #93c5fd;
      cursor: default;
    }
    :host-context([data-jv-edit]) .tk { cursor: text; }

    /* Colon */
    .tc { color: #6b7280; }

    /* Bracket */
    .tb { color: #6b7280; font-weight: 600; }

    /* Comma */
    .comma { color: #4b5563; }

    /* Child count badge */
    .child-count {
      display: inline-block;
      padding: 0 4px;
      margin: 0 2px;
      font-size: 0.6rem;
      font-weight: 600;
      color: #94a3b8;
      background: #1e293b;
      border-radius: 3px;
    }

    /* Values by type */
    .tv { cursor: default; }
    .t-string  { color: #86efac; }
    .t-number  { color: #fde68a; }
    .t-boolean { color: #c4b5fd; }
    .t-null    { color: #6b7280; font-style: italic; }

    .tree-empty {
      color: #4b5563;
      padding: 8px 16px;
      font-style: italic;
    }

    /* ── Insert zone (between rows in edit mode) ───────────── */

    .tree-insert-zone {
      height: 4px;
      position: relative;
      cursor: pointer;
    }
    .tree-insert-zone .insert-line {
      position: absolute;
      top: 50%;
      left: 14px;
      right: 8px;
      height: 0;
      display: flex;
      align-items: center;
      opacity: 0;
      transition: opacity 0.12s;
    }
    .tree-insert-zone:hover .insert-line {
      opacity: 1;
    }
    .tree-insert-zone .insert-line::after {
      content: '';
      position: absolute;
      left: 14px;
      right: 0;
      height: 1px;
      background: #3b82f6;
    }
    .tree-insert-zone .insert-icon {
      position: relative;
      z-index: 1;
      font-size: 12px;
      width: 14px;
      height: 14px;
      line-height: 14px;
      background: #3b82f6;
      color: #fff;
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
      flex-shrink: 0;
    }

    /* ── Path breadcrumb ────────────────────────────────────────── */

    .path-breadcrumb {
      display: flex;
      align-items: center;
      gap: 6px;
      padding: 2px 8px;
      background: #0a0a18;
      border-top: 1px solid #23233a;
      font-family: ui-monospace, monospace;
      font-size: 0.62rem;
    }
    .breadcrumb-text {
      color: #94a3b8;
      flex: 1;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
    .breadcrumb-btn {
      background: #1e293b;
      border: 1px solid #334155;
      border-radius: 3px;
      color: #94a3b8;
      font-family: ui-monospace, monospace;
      font-size: 0.58rem;
      font-weight: 600;
      padding: 1px 5px;
      cursor: pointer;
      text-transform: uppercase;
      letter-spacing: 0.03em;
    }
    .breadcrumb-btn:hover {
      background: #334155;
      color: #e5e7eb;
    }

    /* ── Inline edit ──────────────────────────────────────────── */

    .inline-edit {
      background: #1a1a2e;
      border: 1px solid #3b82f6;
      border-radius: 2px;
      color: #e5e7eb;
      font-family: ui-monospace, monospace;
      font-size: 0.7rem;
      padding: 0 4px;
      outline: none;
      min-width: 40px;
      line-height: 18px;
    }
    .key-edit { color: #93c5fd; }
    .value-edit { min-width: 60px; }

    /* ── Error bar ────────────────────────────────────────────── */

    .jv-error {
      padding: 3px 8px;
      background: rgba(239, 68, 68, 0.12);
      color: #f87171;
      font-size: 0.66rem;
      border-top: 1px solid rgba(239, 68, 68, 0.25);
    }

    /* ── Resize handle ────────────────────────────────────────── */

    .jv-resize {
      height: 6px;
      cursor: ns-resize;
      background: #23233a;
      transition: background 0.12s;
    }
    .jv-resize:hover,
    .jv-resize:active {
      background: rgba(71, 170, 255, 0.45);
    }

    /* ── Search bar ──────────────────────────────────────────── */

    .jv-search-bar {
      display: flex;
      align-items: center;
      gap: 4px;
      padding: 3px 6px;
      background: #0f0f1e;
      border-bottom: 1px solid #23233a;
    }
    .jv-search-bar > mat-icon {
      font-size: 14px; width: 14px; height: 14px; color: #6b7280;
    }
    .jv-search-input {
      flex: 1;
      background: #14142b;
      border: 1px solid #334155;
      border-radius: 3px;
      color: #e5e7eb;
      font-family: ui-monospace, monospace;
      font-size: 0.68rem;
      padding: 2px 6px;
      outline: none;
      min-width: 0;
    }
    .jv-search-input:focus { border-color: #3b82f6; }
    .jv-search-count {
      font-size: 0.62rem;
      color: #94a3b8;
      white-space: nowrap;
      font-family: ui-monospace, monospace;
    }
    .jv-search-nav {
      background: none;
      border: none;
      color: #6b7280;
      padding: 1px;
      cursor: pointer;
      display: inline-flex;
      align-items: center;
      border-radius: 3px;
    }
    .jv-search-nav mat-icon {
      font-size: 14px; width: 14px; height: 14px;
    }
    .jv-search-nav:hover { color: #e5e7eb; background: #374151; }
    .jv-tool-active { color: #3b82f6 !important; }

    /* Search highlighting */
    .jv-text :deep(.search-hl) {
      background: rgba(250, 204, 21, 0.35);
      color: #fef08a;
      border-radius: 1px;
    }
    .tree-row {
      position: relative;
    }
    .tree-row.search-match {
      background: rgba(250, 204, 21, 0.08);
    }
    .tree-row.search-match::before {
      content: '';
      position: absolute;
      left: 0;
      top: 0;
      bottom: 0;
      width: 2px;
      background: #facc15;
    }

    /* ── Validation warnings ─────────────────────────────────── */

    .jv-warnings {
      border-top: 1px solid rgba(245, 158, 11, 0.25);
      background: rgba(245, 158, 11, 0.06);
      padding: 4px 8px;
      max-height: 80px;
      overflow-y: auto;
    }
    .jv-warn-header {
      display: flex;
      align-items: center;
      gap: 4px;
      font-size: 0.64rem;
      font-weight: 600;
      color: #f59e0b;
      margin-bottom: 2px;
    }
    .jv-warn-header mat-icon {
      font-size: 12px; width: 12px; height: 12px;
    }
    .jv-warn-item {
      display: flex;
      gap: 6px;
      font-size: 0.62rem;
      padding: 1px 0;
    }
    .jv-warn-path {
      color: #f59e0b;
      font-family: ui-monospace, monospace;
      font-weight: 600;
      flex-shrink: 0;
    }
    .jv-warn-msg {
      color: #d4d4d8;
    }

    /* ── Graph view ──────────────────────────────────────────── */

    .graph-container {
      flex: 1;
      min-height: 0;
      overflow: hidden;
      cursor: grab;
      background:
        radial-gradient(circle at 1px 1px, #1e1e3a 1px, transparent 0) 0 0 / 20px 20px,
        #14142b;
      -webkit-user-select: none;
      user-select: none;
      position: relative;
    }
    .graph-container:active { cursor: grabbing; }
    .graph-container * { -webkit-user-drag: none; }

    .graph-svg {
      width: 100%;
      height: 100%;
      display: block;
      overflow: visible;
    }

    /* Links */
    .graph-link {
      fill: none;
      stroke: #3a3a5a;
      stroke-width: 1.5;
      opacity: 0.7;
    }

    /* Nodes — table-card style */
    .graph-node {
      cursor: pointer;
      user-select: none;
      -webkit-user-select: none;
    }
    .graph-node-bg {
      fill: #111827;
      stroke: #334155;
      stroke-width: 1.5;
      transition: stroke 0.15s;
    }
    .graph-node:hover .graph-node-bg { stroke: #60a5fa; }
    .graph-node-focused .graph-node-bg { stroke: #3b82f6; stroke-width: 2; }

    .graph-node-object .graph-node-bg,
    .graph-node-object .graph-node-header { stroke: #6366f1; }
    .graph-node-object .graph-node-header,
    .graph-node-object .graph-node-header-fill { fill: #312e81; }
    .graph-node-object:hover .graph-node-bg,
    .graph-node-object.graph-node-focused .graph-node-bg { stroke: #818cf8; }

    .graph-node-array .graph-node-bg,
    .graph-node-array .graph-node-header { stroke: #b45309; }
    .graph-node-array .graph-node-header,
    .graph-node-array .graph-node-header-fill { fill: #451a03; }
    .graph-node-array:hover .graph-node-bg,
    .graph-node-array.graph-node-focused .graph-node-bg { stroke: #fbbf24; }

    /* Header bar (top of card) */
    .graph-node-header {
      fill: #1e293b;
      stroke: none;
    }
    .graph-node-header-fill {
      fill: #1e293b;
      stroke: none;
    }
    .graph-node-sep {
      stroke: #334155;
      stroke-width: 1;
    }

    .graph-node-icon {
      fill: #9ca3af;
      font-family: ui-monospace, monospace;
      font-size: 10px;
      font-weight: 700;
    }
    .graph-node-title {
      fill: #e5e7eb;
      font-family: ui-monospace, monospace;
      font-size: 11px;
      font-weight: 700;
    }
    .graph-node-badge {
      fill: #6b7280;
      font-family: ui-monospace, monospace;
      font-size: 9px;
      font-weight: 700;
      text-anchor: end;
    }

    /* Property rows (table body) */
    .graph-prop-key {
      fill: #93c5fd;
      font-family: ui-monospace, monospace;
      font-size: 10px;
    }
    .graph-prop-val {
      font-family: ui-monospace, monospace;
      font-size: 10px;
      text-anchor: end;
    }
    .gv-string  { fill: #86efac; }
    .gv-number  { fill: #fde68a; }
    .gv-boolean { fill: #c4b5fd; }
    .gv-null    { fill: #6b7280; font-style: italic; }

    /* Collapse toggle circle */
    .graph-toggle {
      fill: #1f2937;
      stroke: #4b5563;
      stroke-width: 1;
      cursor: pointer;
    }
    .graph-toggle:hover { fill: #374151; stroke: #9ca3af; }
    .graph-toggle-text {
      fill: #d1d5db;
      font-size: 11px;
      font-weight: 700;
      cursor: pointer;
      user-select: none;
    }

    /* Add child button */
    .graph-add-btn {
      fill: #065f46;
      stroke: #10b981;
      stroke-width: 1;
      cursor: pointer;
      opacity: 0;
      transition: opacity 0.15s;
    }
    .graph-node:hover .graph-add-btn { opacity: 1; }
    .graph-add-btn:hover { fill: #10b981; }
    .graph-add-text {
      fill: #fff;
      font-size: 11px;
      font-weight: 700;
      cursor: pointer;
      opacity: 0;
      transition: opacity 0.15s;
      user-select: none;
    }
    .graph-node:hover .graph-add-text { opacity: 1; }

    /* Invisible hit-area rect for prop cells */
    .graph-prop-hit {
      fill: transparent;
    }

    /* Edit pencil button — circle bg + icon, hidden until hover */
    .graph-edit-btn {
      cursor: pointer;
      opacity: 0;
      pointer-events: none;
      transition: opacity 0.15s;
    }
    .graph-edit-btn-bg {
      fill: none;
      stroke: none;
    }
    .graph-edit-btn-icon {
      fill: #fbbf24;
      font-size: 14px;
      text-anchor: middle;
      dominant-baseline: central;
    }
    .graph-edit-btn:hover .graph-edit-btn-icon {
      fill: #fde68a;
    }
    /* Show on cell hover (prop rows) */
    .graph-prop-cell:hover .graph-edit-btn {
      opacity: 1;
      pointer-events: auto;
    }
    /* Show on node hover (header key) */
    .graph-node:hover > .graph-edit-icon-header {
      opacity: 1;
      pointer-events: auto;
    }

    /* Inline edit input inside foreignObject */
    .graph-inline-input {
      width: 100%;
      height: 100%;
      box-sizing: border-box;
      padding: 1px 4px;
      margin: 0;
      border: 1px solid #3b82f6;
      border-radius: 3px;
      background: #1e293b;
      color: #e5e7eb;
      font-family: ui-monospace, monospace;
      font-size: 11px;
      font-weight: 700;
      outline: none;
    }
    .graph-inline-input.graph-inline-prop {
      font-size: 10px;
      font-weight: 400;
    }

    /* Search match glow on graph nodes */
    .graph-node-search-match .graph-node-bg {
      stroke: #facc15 !important;
      stroke-width: 2.5;
      filter: drop-shadow(0 0 6px rgba(250, 204, 21, 0.4));
    }

    /* Info overlay */
    .graph-info {
      position: absolute;
      bottom: 6px;
      right: 8px;
      display: flex;
      gap: 8px;
      font-family: ui-monospace, monospace;
      font-size: 0.6rem;
      color: #4b5563;
      pointer-events: none;
    }
  `],
})
export class JsonViewerComponent {
  // ── Inputs ──────────────────────────────────────────────────────────────────

  readonly data = input<unknown>(null);
  readonly mode = input<'view' | 'edit'>('view');
  readonly resizable = input(true);
  readonly initialHeight = input(160);
  readonly minHeight = input(40);
  readonly schema = input<Record<string, unknown> | null>(null);

  // ── Outputs ─────────────────────────────────────────────────────────────────

  @Output() readonly save = new EventEmitter<unknown>();
  @Output() readonly maximizeToggle = new EventEmitter<boolean>();

  private static readonly LS_VIEW_KEY = 'devTools.jsonViewerView';

  // ── State ───────────────────────────────────────────────────────────────────

  readonly activeView = signal<ViewType>(
    (localStorage.getItem(JsonViewerComponent.LS_VIEW_KEY) as ViewType) || 'json'
  );
  readonly currentHeight = signal(160);
  private readonly internalData = signal<unknown>(null);
  /** Whether the current data contains circular references. */
  readonly hasCircular = computed(() => this.hasCircularRef(this.internalData()));
  jsonEditText = '';
  readonly parseError = signal<string | null>(null);

  readonly wordWrap = signal(localStorage.getItem('devTools.jsonWrap') !== 'false');
  readonly minified = signal(false);

  // Tree
  private readonly expandedPaths = signal(new Set<string>());
  readonly editingNodeId = signal<string | null>(null);
  readonly editingField = signal<'key' | 'value'>('value');
  readonly editingText = signal('');
  readonly hoveredPath = signal<(string | number)[] | null>(null);

  // Maximize
  readonly maximized = signal(false);

  // Search
  readonly searchOpen = signal(false);
  readonly searchText = signal('');
  readonly currentMatchIdx = signal(0);

  // Graph
  readonly graphScale = signal(1);
  readonly graphPanX = signal(0);
  readonly graphPanY = signal(0);
  readonly graphCollapsedIds = signal(new Set<string>());
  readonly graphPropsCollapsedIds = signal(new Set<string>());
  readonly graphFocusedId = signal<string | null>(null);
  readonly graphEditingId = signal<string | null>(null);
  readonly graphEditingField = signal<'key' | 'value'>('key');
  readonly graphEditingPropIdx = signal<number | null>(null); // null = editing node key, number = prop index
  readonly graphEditText = signal('');
  private graphDragging = false;
  private graphDidDrag = false;
  private graphDragStartX = 0;
  private graphDragStartY = 0;
  private graphPanStartX = 0;
  private graphPanStartY = 0;

  @ViewChild('contentEl') private contentRef!: ElementRef<HTMLElement>;
  private readonly el = inject(ElementRef);
  readonly barService = inject(DevBottomBarService);

  // ── Computed ─────────────────────────────────────────────────────────────────

  readonly jsonDisplayText = computed(() => {
    const data = this.internalData();
    return this.minified() ? this.stringifyCompact(data) : this.stringify(data);
  });

  readonly highlightedJson = computed(() => this.syntaxHighlight(this.jsonDisplayText()));

  readonly highlightedJsonWithSearch = computed(() => {
    const html = this.highlightedJson();
    const query = this.searchText().trim();
    if (!query) return html;
    return this.highlightSearch(html, query);
  });

  readonly searchMatchCount = computed(() => {
    const query = this.searchText().toLowerCase().trim();
    if (!query) return 0;
    const text = this.jsonDisplayText();
    if (!text) return 0;
    const escaped = query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    return (text.match(new RegExp(escaped, 'gi')) || []).length;
  });

  readonly searchMatchPathSet = computed(() => {
    const query = this.searchText().toLowerCase().trim();
    if (!query) return new Set<string>();
    const matches = new Set<string>();
    this.findSearchMatches(this.internalData(), [], query, matches);
    return matches;
  });

  /** Graph node IDs that contain at least one search match (node key, prop key, or prop value). */
  readonly graphSearchMatchIds = computed((): Set<string> => {
    const query = this.searchText().toLowerCase().trim();
    if (!query) return new Set();
    const matchIds = new Set<string>();
    for (const node of this.graphAllNodes()) {
      if (node.key.toLowerCase().includes(query)) { matchIds.add(node.id); continue; }
      for (const prop of node.props) {
        if (prop.key.toLowerCase().includes(query) || prop.value.toLowerCase().includes(query)) {
          matchIds.add(node.id);
          break;
        }
      }
    }
    return matchIds;
  });

  /** Ordered list of graph node IDs that match the search, for navigation. */
  readonly graphSearchMatchList = computed((): string[] => {
    const ids = this.graphSearchMatchIds();
    if (ids.size === 0) return [];
    return this.graphAllNodes().filter(n => ids.has(n.id)).map(n => n.id);
  });

  readonly validationWarnings = computed((): { path: string; message: string }[] => {
    const s = this.schema();
    const data = this.internalData();
    if (!s || data === null || data === undefined) return [];
    return this.validateSchema(data, s, []);
  });

  readonly visibleNodes = computed((): FlatNode[] => {
    return this.buildFlatTree(this.internalData(), this.expandedPaths(), this.mode() === 'edit');
  });

  // ── Graph computed ───────────────────────────────────────────────────────────

  private readonly graphAllNodes = computed((): GraphNode[] => {
    const data = this.internalData();
    if (data === null || data === undefined) return [];
    const type = this.getJsonType(data);
    // If root is a primitive, wrap it in a single node
    if (type !== 'object' && type !== 'array') {
      const val = this.formatLeafValue(data, type);
      const truncated = val.length > 40 ? val.slice(0, 39) + '…' : val;
      const w = Math.min(320, Math.max(140, Math.ceil(('(value)'.length + truncated.length) * 6.2 + 24)));
      return [{
        id: 'root', key: 'root', path: [], type: 'object',
        props: [{ key: '(value)', value: truncated, type, path: [] }],
        x: 0, y: 0, children: [], parent: null, collapsed: false, width: w, height: 40,
      }];
    }
    const nodes: GraphNode[] = [];
    this.buildGraphNodes(data, 'root', 'root', [], null, nodes, new WeakSet());
    return nodes;
  });

  /** Layout: assign x/y to every visible node using a simple tree layout. */
  readonly graphLayout = computed((): { nodes: GraphNode[]; links: GraphLink[] } => {
    const all = this.graphAllNodes();
    if (all.length === 0) return { nodes: [], links: [] };

    const collapsed = this.graphCollapsedIds();
    const propsCollapsed = this.graphPropsCollapsedIds();
    const nodeMap = new Map<string, GraphNode>();
    for (const n of all) nodeMap.set(n.id, n);

    const HEADER_H = 24;
    const ROW_H = 16;

    // Filter to visible nodes (not hidden by collapsed ancestor)
    const visible: GraphNode[] = [];
    const hiddenIds = new Set<string>();

    const queue = [all[0]];
    while (queue.length > 0) {
      const node = queue.shift()!;
      if (hiddenIds.has(node.id)) continue;
      const hideProps = propsCollapsed.has(node.id);
      const visibleProps = hideProps ? [] : node.props;
      const bodyH = visibleProps.length > 0 ? visibleProps.length * ROW_H + 8 : 4;
      const gn: GraphNode = {
        ...node,
        collapsed: collapsed.has(node.id),
        props: visibleProps,
        height: HEADER_H + bodyH,
      };
      visible.push(gn);
      if (collapsed.has(node.id)) {
        // Mark all descendants as hidden
        const markHidden = (id: string) => {
          hiddenIds.add(id);
          const n = nodeMap.get(id);
          if (n) n.children.forEach(markHidden);
        };
        node.children.forEach(markHidden);
      } else {
        for (const cid of node.children) {
          const cn = nodeMap.get(cid);
          if (cn) queue.push(cn);
        }
      }
    }

    // Simple tree layout — use each node's own height (computed from props count)
    const H_GAP = 60;
    const V_GAP = 14;

    const visMap = new Map<string, GraphNode>();
    for (const n of visible) visMap.set(n.id, n);

    // Calculate subtree heights for layout
    const subtreeHeight = (id: string): number => {
      const n = visMap.get(id);
      if (!n) return 0;
      const visibleChildren = n.children.filter(c => visMap.has(c));
      if (visibleChildren.length === 0) return n.height;
      let total = 0;
      for (const cid of visibleChildren) {
        total += subtreeHeight(cid);
      }
      total += (visibleChildren.length - 1) * V_GAP;
      return Math.max(n.height, total);
    };

    const layout = (id: string, x: number, yStart: number): void => {
      const n = visMap.get(id);
      if (!n) return;
      const visibleChildren = n.children.filter(c => visMap.has(c));
      const totalH = subtreeHeight(id);
      n.x = x;
      n.y = yStart + (totalH - n.height) / 2;

      let childY = yStart;
      for (const cid of visibleChildren) {
        const ch = subtreeHeight(cid);
        layout(cid, x + n.width + H_GAP, childY);
        childY += ch + V_GAP;
      }
    };

    if (visible.length > 0) {
      const totalH = subtreeHeight(visible[0].id);
      layout(visible[0].id, 40, 40);
    }

    // Build links
    const links: GraphLink[] = [];
    for (const n of visible) {
      for (const cid of n.children) {
        if (visMap.has(cid)) {
          links.push({ from: n.id, to: cid });
        }
      }
    }

    return { nodes: visible, links };
  });

  readonly graphVisibleNodes = computed(() => this.graphLayout().nodes);
  readonly graphLinks = computed(() => this.graphLayout().links);

  readonly graphTransform = computed((): string => {
    const s = this.graphScale();
    const px = this.graphPanX();
    const py = this.graphPanY();
    return `translate(${px},${py}) scale(${s})`;
  });

  constructor() {
    effect(() => {
      const d = this.data();
      this.internalData.set(this.deepClone(d));
      this.jsonEditText = this.stringify(d);
      // Expand nodes — if the tree is small expand everything,
      // otherwise only expand the first two levels to avoid DOM overload.
      const allPaths = new Set<string>();
      this.collectExpandable(d, [], allPaths);
      if (allPaths.size <= 200) {
        this.expandedPaths.set(allPaths);
      } else {
        const shallow = new Set<string>();
        this.collectExpandable(d, [], shallow, undefined, 2);
        this.expandedPaths.set(shallow);
      }
      this.parseError.set(null);
      // Apply smart collapse for graph view when data arrives
      if (this.activeView() === 'graph') {
        setTimeout(() => {
          const { childrenCollapsed, propsCollapsed } = this.computeSmartCollapsed();
          this.graphCollapsedIds.set(childrenCollapsed);
          this.graphPropsCollapsedIds.set(propsCollapsed);
          this.graphFocusRoot();
        }, 50);
      }
    });
    effect(() => {
      this.currentHeight.set(this.initialHeight());
    });
  }

  // ── Toolbar actions ─────────────────────────────────────────────────────────

  toggleWrap(): void {
    const next = !this.wordWrap();
    this.wordWrap.set(next);
    localStorage.setItem('devTools.jsonWrap', String(next));
  }

  toggleMinify(): void {
    this.minified.update(v => !v);
    if (this.mode() === 'edit') {
      this.jsonEditText = this.minified()
        ? this.stringifyCompact(this.internalData())
        : this.stringify(this.internalData());
    }
  }

  downloadJson(): void {
    const text = this.stringify(this.internalData());
    const blob = new Blob([text], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'data.json';
    a.click();
    URL.revokeObjectURL(url);
  }

  // ── Path breadcrumb ────────────────────────────────────────────────────────

  formatPath(path: (string | number)[]): string {
    if (path.length === 0) return '$';
    return path.map((p, i) => typeof p === 'number' ? `[${p}]` : (i === 0 ? p : `.${p}`)).join('');
  }

  copyPath(path: (string | number)[]): void {
    navigator.clipboard.writeText(this.formatPath(path));
  }

  copyAccessor(path: (string | number)[]): void {
    const accessor = path.map(p => typeof p === 'number' ? `[${p}]` : `['${p}']`).join('');
    navigator.clipboard.writeText('data' + accessor);
  }

  copyValueAt(path: (string | number)[]): void {
    const val = this.getAtPath(this.internalData(), path);
    navigator.clipboard.writeText(this.stringify(val));
  }

  // ── Search ─────────────────────────────────────────────────────────────────

  toggleSearch(): void {
    const next = !this.searchOpen();
    this.searchOpen.set(next);
    if (!next) {
      this.searchText.set('');
      this.currentMatchIdx.set(0);
    } else {
      setTimeout(() => {
        const inp = this.el.nativeElement.querySelector('.jv-search-input') as HTMLInputElement;
        if (inp) inp.focus();
      });
    }
  }

  onSearchInput(value: string): void {
    this.searchText.set(value);
    this.currentMatchIdx.set(0);
    // In graph view, auto-focus first match
    if (this.activeView() === 'graph' && value.trim()) {
      setTimeout(() => this.focusGraphSearchMatch());
    }
  }

  nextMatch(): void {
    if (this.activeView() === 'graph') {
      const list = this.graphSearchMatchList();
      if (list.length === 0) return;
      this.currentMatchIdx.update(i => (i + 1) % list.length);
      this.focusGraphSearchMatch();
      return;
    }
    const count = this.searchMatchCount();
    if (count === 0) return;
    this.currentMatchIdx.update(i => (i + 1) % count);
  }

  prevMatch(): void {
    if (this.activeView() === 'graph') {
      const list = this.graphSearchMatchList();
      if (list.length === 0) return;
      this.currentMatchIdx.update(i => (i - 1 + list.length) % list.length);
      this.focusGraphSearchMatch();
      return;
    }
    const count = this.searchMatchCount();
    if (count === 0) return;
    this.currentMatchIdx.update(i => (i - 1 + count) % count);
  }

  private focusGraphSearchMatch(): void {
    const list = this.graphSearchMatchList();
    const idx = this.currentMatchIdx();
    if (idx >= list.length) return;
    const nodeId = list[idx];
    const node = this.graphVisibleNodes().find(n => n.id === nodeId);
    if (node) {
      this.graphFocusedId.set(node.id);
      this.focusGraphNode(node);
    }
  }

  isSearchMatch(node: FlatNode): boolean {
    return this.searchMatchPathSet().has(this.pathKey(node.path));
  }

  isGraphSearchMatch(node: GraphNode): boolean {
    return this.graphSearchMatchIds().has(node.id);
  }

  // ── View switching ──────────────────────────────────────────────────────────

  setView(view: ViewType): void {
    if (view === this.activeView()) return;
    this.cancelEdit();
    this.cancelGraphEdit();

    if (this.mode() === 'edit') {
      if (this.activeView() === 'json') {
        if (!this.syncTextToData()) return;
      } else if (view === 'json') {
        this.jsonEditText = this.stringify(this.internalData());
      }
    }

    // Smart collapse + center on root when switching to graph
    if (view === 'graph') {
      const { childrenCollapsed, propsCollapsed } = this.computeSmartCollapsed();
      this.graphCollapsedIds.set(childrenCollapsed);
      this.graphPropsCollapsedIds.set(propsCollapsed);
      setTimeout(() => this.graphFocusRoot(), 50);
    }
    this.activeView.set(view);
    localStorage.setItem(JsonViewerComponent.LS_VIEW_KEY, view);
  }

  // ── Tree operations ─────────────────────────────────────────────────────────

  toggleNode(node: FlatNode): void {
    const key = this.pathKey(node.path);
    this.expandedPaths.update(set => {
      const next = new Set(set);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  }

  expandAll(): void {
    const paths = new Set<string>();
    this.collectExpandable(this.internalData(), [], paths);
    this.expandedPaths.set(paths);
  }

  collapseAll(): void {
    this.expandedPaths.set(new Set());
  }

  // ── Inline editing ──────────────────────────────────────────────────────────

  isEditingNode(node: FlatNode, field: 'key' | 'value'): boolean {
    return this.mode() === 'edit'
      && this.editingNodeId() === node.id
      && this.editingField() === field;
  }

  startEdit(node: FlatNode, field: 'key' | 'value'): void {
    if (this.mode() !== 'edit') return;
    // Can't rename array indices
    if (field === 'key' && node.path.length > 0 && typeof node.path[node.path.length - 1] === 'number') return;
    // Can't edit root key
    if (field === 'key' && node.path.length === 0) return;

    this.editingNodeId.set(node.id);
    this.editingField.set(field);
    this.editingText.set(field === 'key' ? node.key : node.displayValue);

    setTimeout(() => {
      const inp = this.el.nativeElement.querySelector('.inline-edit') as HTMLInputElement;
      if (inp) { inp.focus(); inp.select(); }
    });
  }

  cancelEdit(): void {
    this.editingNodeId.set(null);
  }

  commitKeyEdit(node: FlatNode): void {
    const newKey = this.editingText().trim();
    if (!newKey || newKey === node.key) { this.cancelEdit(); return; }

    const parentPath = node.path.slice(0, -1);
    const oldKey = node.path[node.path.length - 1] as string;
    const data = this.deepClone(this.internalData());
    const parent = this.getAtPath(data, parentPath) as Record<string, unknown>;
    if (!parent || typeof parent !== 'object' || Array.isArray(parent)) { this.cancelEdit(); return; }

    // Rebuild preserving key order
    const entries = Object.entries(parent);
    for (const k of Object.keys(parent)) delete parent[k];
    for (const [k, v] of entries) {
      parent[k === oldKey ? newKey : k] = v;
    }

    this.internalData.set(data);
    this.cancelEdit();
  }

  commitValueEdit(node: FlatNode): void {
    const raw = this.editingText();
    let parsed: unknown;
    try {
      parsed = JSON.parse(raw);
    } catch {
      parsed = raw; // treat as string
    }

    const data = this.deepClone(this.internalData());
    this.setAtPath(data, node.path, parsed);
    this.internalData.set(data);
    this.cancelEdit();
  }

  /** Insert a new field/element at the position indicated by an insert-zone node. */
  insertAtNode(node: FlatNode): void {
    if (this.mode() !== 'edit' || node.kind !== 'insert') return;
    const data = this.deepClone(this.internalData());
    const parent = node.path.length === 0 ? data : this.getAtPath(data, node.path);
    if (parent === null || parent === undefined) return;

    if (Array.isArray(parent)) {
      parent.splice(node.insertIndex!, 0, null);
    } else if (typeof parent === 'object') {
      // Find a unique key name
      const obj = parent as Record<string, unknown>;
      let newKey = 'newKey';
      let n = 1;
      while (newKey in obj) { newKey = `newKey${n++}`; }
      // Insert at the right position by rebuilding
      const entries = Object.entries(obj);
      for (const k of Object.keys(obj)) delete obj[k];
      entries.splice(node.insertIndex!, 0, [newKey, null]);
      for (const [k, v] of entries) obj[k] = v;
    }

    this.internalData.set(data);
  }

  // ── Maximize ────────────────────────────────────────────────────────────────

  toggleMaximize(): void {
    const next = !this.maximized();
    this.maximized.set(next);
    this.maximizeToggle.emit(next);
  }

  // ── Graph operations ───────────────────────────────────────────────────────

  graphFocusRoot(): void {
    const nodes = this.graphVisibleNodes();
    const root = nodes.find(n => n.id === 'root') ?? nodes[0];
    if (root) this.focusGraphNode(root);
  }

  graphZoomIn(): void {
    this.graphScale.update(s => Math.min(s * 1.25, 4));
  }

  graphZoomOut(): void {
    this.graphScale.update(s => Math.max(s / 1.25, 0.15));
  }

  graphFit(): void {
    const nodes = this.graphVisibleNodes();
    if (nodes.length === 0) return;
    const el = this.contentRef?.nativeElement;
    if (!el) return;

    let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
    for (const n of nodes) {
      minX = Math.min(minX, n.x);
      minY = Math.min(minY, n.y);
      maxX = Math.max(maxX, n.x + n.width);
      maxY = Math.max(maxY, n.y + n.height);
    }

    const pad = 40;
    const graphW = maxX - minX + pad * 2;
    const graphH = maxY - minY + pad * 2;
    const viewW = el.clientWidth;
    const viewH = el.clientHeight;
    const scale = Math.min(viewW / graphW, viewH / graphH, 2);

    this.graphScale.set(scale);
    this.graphPanX.set((viewW - graphW * scale) / 2 + (pad - minX) * scale);
    this.graphPanY.set((viewH - graphH * scale) / 2 + (pad - minY) * scale);
  }

  onGraphMouseDown(e: MouseEvent): void {
    this.graphDragging = true;
    this.graphDidDrag = false;
    this.graphDragStartX = e.clientX;
    this.graphDragStartY = e.clientY;
    this.graphPanStartX = this.graphPanX();
    this.graphPanStartY = this.graphPanY();
  }

  onGraphMouseMove(e: MouseEvent): void {
    if (!this.graphDragging) return;
    const dx = e.clientX - this.graphDragStartX;
    const dy = e.clientY - this.graphDragStartY;
    if (Math.abs(dx) > 3 || Math.abs(dy) > 3) this.graphDidDrag = true;
    this.graphPanX.set(this.graphPanStartX + dx);
    this.graphPanY.set(this.graphPanStartY + dy);
  }

  onGraphMouseUp(_e: MouseEvent): void {
    this.graphDragging = false;
    // Dismiss inline edit when clicking outside the input
    if (this.graphEditingId() !== null) {
      this.cancelGraphEdit();
    }
  }

  onGraphWheel(e: WheelEvent): void {
    e.preventDefault();
    const factor = e.deltaY < 0 ? 1.1 : 1 / 1.1;
    const oldScale = this.graphScale();
    const newScale = Math.max(0.15, Math.min(4, oldScale * factor));

    // Zoom pivoting around mouse position
    const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
    const mx = e.clientX - rect.left;
    const my = e.clientY - rect.top;

    // The world-point under the mouse before zoom: wx = (mx - panX) / oldScale
    // After zoom we want the same world-point under the mouse:
    // mx = panX' + wx * newScale  =>  panX' = mx - wx * newScale
    const wx = (mx - this.graphPanX()) / oldScale;
    const wy = (my - this.graphPanY()) / oldScale;
    this.graphScale.set(newScale);
    this.graphPanX.set(mx - wx * newScale);
    this.graphPanY.set(my - wy * newScale);
  }

  onGraphDblClick(e: MouseEvent): void {
    if ((e.target as HTMLElement).closest('.graph-node')) return;
    this.graphFit();
  }

  onGraphNodeClick(node: GraphNode, e: MouseEvent): void {
    e.stopPropagation();
    if (this.graphDidDrag) return; // was a pan gesture, not a click
    this.graphFocusedId.set(this.graphFocusedId() === node.id ? null : node.id);
    this.focusGraphNode(node);
  }

  private graphPanAnimId: number | null = null;

  private focusGraphNode(node: GraphNode): void {
    const el = this.contentRef?.nativeElement;
    if (!el) return;

    const targetScale = 1.3;
    const cx = node.x + node.width / 2;
    const cy = node.y + node.height / 2;
    const targetPanX = el.clientWidth / 2 - cx * targetScale;
    const targetPanY = el.clientHeight / 2 - cy * targetScale;

    // Cancel any running animation
    if (this.graphPanAnimId !== null) {
      cancelAnimationFrame(this.graphPanAnimId);
      this.graphPanAnimId = null;
    }

    const startScale = this.graphScale();
    const startPanX = this.graphPanX();
    const startPanY = this.graphPanY();
    const duration = 350; // ms
    const startTime = performance.now();

    const animate = (now: number) => {
      const elapsed = now - startTime;
      const t = Math.min(elapsed / duration, 1);
      // Ease-out cubic
      const ease = 1 - Math.pow(1 - t, 3);

      this.graphScale.set(startScale + (targetScale - startScale) * ease);
      this.graphPanX.set(startPanX + (targetPanX - startPanX) * ease);
      this.graphPanY.set(startPanY + (targetPanY - startPanY) * ease);

      if (t < 1) {
        this.graphPanAnimId = requestAnimationFrame(animate);
      } else {
        this.graphPanAnimId = null;
      }
    };

    this.graphPanAnimId = requestAnimationFrame(animate);
  }

  onGraphNodeDblClick(node: GraphNode, e: MouseEvent): void {
    e.stopPropagation();
  }

  toggleGraphNode(node: GraphNode, e: MouseEvent): void {
    e.stopPropagation();
    // Anchor point: children toggle sits at (node.x + node.width, node.y + 12)
    const oldAnchorX = node.x + node.width;
    const oldAnchorY = node.y + 12;

    this.graphCollapsedIds.update(set => {
      const next = new Set(set);
      if (next.has(node.id)) next.delete(node.id); else next.add(node.id);
      return next;
    });

    this.compensateGraphPan(node.id, oldAnchorX, oldAnchorY, 'children');
  }

  toggleGraphProps(node: GraphNode, e: MouseEvent): void {
    e.stopPropagation();
    // Anchor point: props toggle sits at (node.x + 0, node.y + 12)
    const oldAnchorX = node.x;
    const oldAnchorY = node.y + 12;

    this.graphPropsCollapsedIds.update(set => {
      const next = new Set(set);
      if (next.has(node.id)) next.delete(node.id); else next.add(node.id);
      return next;
    });

    this.compensateGraphPan(node.id, oldAnchorX, oldAnchorY, 'props');
  }

  /**
   * After a collapse/expand toggle, adjust pan so the toggle button stays
   * in the same screen position, preventing disorientation.
   */
  private compensateGraphPan(
    nodeId: string,
    oldAnchorX: number,
    oldAnchorY: number,
    toggle: 'children' | 'props',
  ): void {
    const updated = this.graphVisibleNodes().find(n => n.id === nodeId);
    if (!updated) return;

    const newAnchorX = toggle === 'children' ? updated.x + updated.width : updated.x;
    const newAnchorY = updated.y + 12;

    const scale = this.graphScale();
    const dx = (oldAnchorX - newAnchorX) * scale;
    const dy = (oldAnchorY - newAnchorY) * scale;

    if (dx !== 0 || dy !== 0) {
      this.graphPanX.update(v => v + dx);
      this.graphPanY.update(v => v + dy);
    }
  }

  isPropsCollapsed(node: GraphNode): boolean {
    return this.graphPropsCollapsedIds().has(node.id);
  }

  /** Whether the original node (before layout filtering) has primitive props. */
  hasPropsForNode(node: GraphNode): boolean {
    const orig = this.graphAllNodes().find(n => n.id === node.id);
    return !!orig && orig.props.length > 0;
  }

  getOriginalPropCount(node: GraphNode): number {
    const orig = this.graphAllNodes().find(n => n.id === node.id);
    return orig ? orig.props.length : node.props.length;
  }

  graphExpandAll(): void {
    this.graphCollapsedIds.set(new Set());
    this.graphPropsCollapsedIds.set(new Set());
  }

  graphCollapseAll(): void {
    const ids = this.graphAllNodes().map(n => n.id);
    this.graphCollapsedIds.set(new Set(ids));
    this.graphPropsCollapsedIds.set(new Set(ids));
  }

  /** Smart collapse: objects stay expanded, but for arrays all items except the
   *  first are fully minimized (children collapsed + props collapsed). */
  graphSmartCollapse(): void {
    const { childrenCollapsed, propsCollapsed } = this.computeSmartCollapsed();
    this.graphCollapsedIds.set(childrenCollapsed);
    this.graphPropsCollapsedIds.set(propsCollapsed);
  }

  private computeSmartCollapsed(): { childrenCollapsed: Set<string>; propsCollapsed: Set<string> } {
    const all = this.graphAllNodes();
    const childrenCollapsed = new Set<string>();
    const propsCollapsed = new Set<string>();

    for (const node of all) {
      if (node.type !== 'array') continue;
      // Fully minimize every child of this array except the first
      for (let i = 1; i < node.children.length; i++) {
        const cid = node.children[i];
        childrenCollapsed.add(cid);
        propsCollapsed.add(cid);
      }
    }
    return { childrenCollapsed, propsCollapsed };
  }

  cancelGraphEdit(): void {
    this.graphEditingId.set(null);
    this.graphEditingPropIdx.set(null);
  }

  private focusAndSelectEditInput(): void {
    setTimeout(() => {
      const input = this.el.nativeElement.querySelector('.graph-inline-input') as HTMLInputElement | null;
      if (input) { input.focus(); input.select(); }
    });
  }

  // ── Start editing ──

  startEditNodeKey(node: GraphNode, e: MouseEvent): void {
    e.stopPropagation();

    if (this.mode() !== 'edit') return;
    // Cannot rename root or array items (numeric keys)
    if (node.path.length === 0) return;
    const lastSeg = node.path[node.path.length - 1];
    if (typeof lastSeg === 'number') return;
    this.graphEditingId.set(node.id);
    this.graphEditingPropIdx.set(null);
    this.graphEditingField.set('key');
    this.graphEditText.set(node.key);
    this.focusAndSelectEditInput();
  }

  startEditPropKey(node: GraphNode, prop: GraphProp, idx: number, e: MouseEvent): void {
    e.stopPropagation();

    if (this.mode() !== 'edit') return;
    // Cannot rename array item keys (numeric indices)
    if (node.type === 'array') return;
    this.graphEditingId.set(node.id);
    this.graphEditingPropIdx.set(idx);
    this.graphEditingField.set('key');
    this.graphEditText.set(prop.key);
    this.focusAndSelectEditInput();
  }

  startEditPropValue(node: GraphNode, prop: GraphProp, idx: number, e: MouseEvent): void {
    e.stopPropagation();

    if (this.mode() !== 'edit') return;
    this.graphEditingId.set(node.id);
    this.graphEditingPropIdx.set(idx);
    this.graphEditingField.set('value');
    // Show the raw value (without quotes for strings) for easier editing
    const raw = this.getAtPath(this.internalData(), prop.path);
    this.graphEditText.set(typeof raw === 'string' ? raw : JSON.stringify(raw));
    this.focusAndSelectEditInput();
  }

  // ── Commit editing ──

  commitGraphKeyEdit(node: GraphNode): void {
    const newKey = this.graphEditText().trim();
    if (!newKey || newKey === node.key) { this.cancelGraphEdit(); return; }

    const parentPath = node.path.slice(0, -1);
    const oldKey = node.path[node.path.length - 1] as string;
    const data = this.deepClone(this.internalData());
    const parent = parentPath.length === 0 ? data : this.getAtPath(data, parentPath);
    if (!parent || typeof parent !== 'object' || Array.isArray(parent)) { this.cancelGraphEdit(); return; }

    const obj = parent as Record<string, unknown>;
    const entries = Object.entries(obj);
    for (const k of Object.keys(obj)) delete obj[k];
    for (const [k, v] of entries) obj[k === oldKey ? newKey : k] = v;

    this.internalData.set(data);
    this.cancelGraphEdit();
  }

  commitPropKeyEdit(node: GraphNode, prop: GraphProp): void {
    const newKey = this.graphEditText().trim();
    if (!newKey || newKey === prop.key) { this.cancelGraphEdit(); return; }

    const parentPath = prop.path.slice(0, -1);
    const oldKey = prop.path[prop.path.length - 1] as string;
    const data = this.deepClone(this.internalData());
    const parent = parentPath.length === 0 ? data : this.getAtPath(data, parentPath);
    if (!parent || typeof parent !== 'object' || Array.isArray(parent)) { this.cancelGraphEdit(); return; }

    const obj = parent as Record<string, unknown>;
    const entries = Object.entries(obj);
    for (const k of Object.keys(obj)) delete obj[k];
    for (const [k, v] of entries) obj[k === oldKey ? newKey : k] = v;

    this.internalData.set(data);
    this.cancelGraphEdit();
  }

  commitPropValueEdit(node: GraphNode, prop: GraphProp): void {
    const raw = this.graphEditText();
    let parsed: unknown;
    try { parsed = JSON.parse(raw); } catch { parsed = raw; }

    const data = this.deepClone(this.internalData());
    this.setAtPath(data, prop.path, parsed);
    this.internalData.set(data);
    this.cancelGraphEdit();
  }

  commitGraphValueEdit(node: GraphNode): void {
    const raw = this.graphEditText();
    let parsed: unknown;
    try { parsed = JSON.parse(raw); } catch { parsed = raw; }

    const data = this.deepClone(this.internalData());
    this.setAtPath(data, node.path, parsed);
    this.internalData.set(data);
    this.cancelGraphEdit();
  }

  addGraphChild(node: GraphNode, e: MouseEvent): void {
    e.stopPropagation();
    if (this.mode() !== 'edit') return;

    const data = this.deepClone(this.internalData());
    const target = node.path.length === 0 ? data : this.getAtPath(data, node.path);
    if (!target || typeof target !== 'object') return;

    if (Array.isArray(target)) {
      target.push({});
    } else {
      const obj = target as Record<string, unknown>;
      let newKey = 'newKey';
      let n = 1;
      while (newKey in obj) { newKey = `newKey${n++}`; }
      obj[newKey] = null;
    }

    this.internalData.set(data);
  }

  getGraphLinkPath(link: GraphLink): string {
    const nodes = this.graphVisibleNodes();
    const from = nodes.find(n => n.id === link.from);
    const to = nodes.find(n => n.id === link.to);
    if (!from || !to) return '';

    const x1 = from.x + from.width;
    const y1 = from.y + from.height / 2;
    const x2 = to.x;
    const y2 = to.y + to.height / 2;
    const mx = (x1 + x2) / 2;
    return `M${x1},${y1} C${mx},${y1} ${mx},${y2} ${x2},${y2}`;
  }

  // ── Save ────────────────────────────────────────────────────────────────────

  onSave(): void {
    if (this.activeView() === 'json') {
      if (!this.syncTextToData()) return;
    }
    this.save.emit(this.deepClone(this.internalData()));
  }

  // ── Resize ──────────────────────────────────────────────────────────────────

  startResize(event: MouseEvent): void {
    event.preventDefault();
    event.stopPropagation();
    const startY = event.clientY;
    const isMax = this.maximized();
    const startH = isMax ? this.barService.heightPx$.value : this.currentHeight();
    const min = isMax ? 120 : this.minHeight();

    const onMove = (e: MouseEvent) => {
      if (isMax) {
        // Dragging up grows the bar (anchored at bottom)
        this.barService.setHeightPx(Math.max(min, startH + (startY - e.clientY)));
      } else {
        this.currentHeight.set(Math.max(min, startH + (e.clientY - startY)));
      }
    };
    const onUp = () => {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
    document.body.style.cursor = 'ns-resize';
    document.body.style.userSelect = 'none';
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
  }

  // ── Tree building ───────────────────────────────────────────────────────────

  private buildFlatTree(data: unknown, expanded: Set<string>, editable = false): FlatNode[] {
    const nodes: FlatNode[] = [];
    if (data === null || data === undefined) return nodes;

    const type = this.getJsonType(data);
    if (type !== 'object' && type !== 'array') {
      nodes.push({
        id: '$root',
        kind: 'leaf',
        path: [],
        key: '',
        depth: 0,
        type,
        childCount: 0,
        displayValue: this.formatLeafValue(data, type),
        hasComma: false,
      });
      return nodes;
    }

    this.walkTree(data, '', 0, [], true, expanded, nodes, editable, new WeakSet());
    return nodes;
  }

  private walkTree(
    value: unknown,
    key: string,
    depth: number,
    path: (string | number)[],
    isLast: boolean,
    expanded: Set<string>,
    out: FlatNode[],
    editable: boolean,
    seen: WeakSet<object>,
  ): void {
    const type = this.getJsonType(value);
    const pk = this.pathKey(path);
    const id = pk || '$root';

    if (type === 'object' || type === 'array') {
      // Circular reference guard
      const obj = value as object;
      if (seen.has(obj)) {
        out.push({ id, kind: 'leaf', path, key, depth, type: 'string', childCount: 0, displayValue: '"[Circular reference]"', hasComma: !isLast });
        return;
      }
      seen.add(obj);

      const entries = type === 'object'
        ? Object.entries(value as object)
        : (value as unknown[]).map((v, i) => [i, v] as [number, unknown]);
      const count = entries.length;
      const isExp = expanded.has(pk);

      if (isExp) {
        out.push({ id, kind: 'open', path, key, depth, type, childCount: count, displayValue: '', hasComma: false });
        if (editable && count === 0) {
          out.push({ id: id + '\x00i0', kind: 'insert', path, key: '', depth: depth + 1, type, childCount: 0, displayValue: '', hasComma: false, insertIndex: 0 });
        }
        entries.forEach(([k, v], i) => {
          if (editable && i === 0) {
            out.push({ id: id + '\x00i0', kind: 'insert', path, key: '', depth: depth + 1, type, childCount: 0, displayValue: '', hasComma: false, insertIndex: 0 });
          }
          const childKey = type === 'array' ? String(k) : (k as string);
          const childPath = [...path, k];
          this.walkTree(v, childKey, depth + 1, childPath, i === entries.length - 1, expanded, out, editable, seen);
          if (editable) {
            out.push({ id: id + '\x00i' + (i + 1), kind: 'insert', path, key: '', depth: depth + 1, type, childCount: 0, displayValue: '', hasComma: false, insertIndex: i + 1 });
          }
        });
        out.push({ id: id + '\x00c', kind: 'close', path, key: '', depth, type, childCount: 0, displayValue: '', hasComma: !isLast });
      } else {
        out.push({ id, kind: 'collapsed', path, key, depth, type, childCount: count, displayValue: '', hasComma: !isLast });
      }

      seen.delete(obj);
    } else {
      out.push({ id, kind: 'leaf', path, key, depth, type, childCount: 0, displayValue: this.formatLeafValue(value, type), hasComma: !isLast });
    }
  }

  private collectExpandable(value: unknown, path: (string | number)[], out: Set<string>, seen?: WeakSet<object>, maxDepth?: number): void {
    if (maxDepth !== undefined && path.length >= maxDepth) return;
    const type = this.getJsonType(value);
    if (type === 'object' || type === 'array') {
      const obj = value as object;
      const s = seen ?? new WeakSet();
      if (s.has(obj)) return;
      s.add(obj);
      out.add(this.pathKey(path));
      if (type === 'object') {
        for (const [k, v] of Object.entries(obj)) {
          this.collectExpandable(v, [...path, k], out, s, maxDepth);
        }
      } else {
        (value as unknown[]).forEach((v, i) => this.collectExpandable(v, [...path, i], out, s, maxDepth));
      }
    }
  }

  // ── Helpers ─────────────────────────────────────────────────────────────────

  private pathKey(path: (string | number)[]): string {
    return path.join('\x00');
  }

  private getJsonType(v: unknown): JsonType {
    if (v === null || v === undefined) return 'null';
    if (Array.isArray(v)) return 'array';
    switch (typeof v) {
      case 'object':  return 'object';
      case 'string':  return 'string';
      case 'number':  return 'number';
      case 'boolean': return 'boolean';
      default:        return 'string';
    }
  }

  private formatLeafValue(v: unknown, type: JsonType): string {
    switch (type) {
      case 'string':  return `"${v}"`;
      case 'number':
      case 'boolean': return String(v);
      case 'null':    return 'null';
      default:        return String(v);
    }
  }

  private stringify(v: unknown): string {
    if (v === null || v === undefined) return '';
    if (typeof v === 'string') return v;
    try { return JSON.stringify(v, this.safeReplacer(), 2); } catch { return String(v); }
  }

  private deepClone<T>(v: T): T {
    if (v === null || v === undefined) return v;
    try { return JSON.parse(JSON.stringify(v, this.safeReplacer())); } catch { return v; }
  }

  private getAtPath(data: unknown, path: (string | number)[]): unknown {
    let current: any = data;
    for (const p of path) {
      if (current === null || current === undefined) return undefined;
      current = current[p];
    }
    return current;
  }

  private setAtPath(data: unknown, path: (string | number)[], value: unknown): void {
    if (path.length === 0) return;
    let current: any = data;
    for (let i = 0; i < path.length - 1; i++) {
      current = current[path[i]];
    }
    current[path[path.length - 1]] = value;
  }

  private stringifyCompact(v: unknown): string {
    if (v === null || v === undefined) return '';
    if (typeof v === 'string') return v;
    try { return JSON.stringify(v, this.safeReplacer()); } catch { return String(v); }
  }

  private syntaxHighlight(json: string): string {
    if (!json) return '';
    return json.replace(/("(?:\\.|[^"\\])*")\s*:/g, '<span class="hl-key">$1</span>:')
               .replace(/:\s*("(?:\\.|[^"\\])*")/g, ': <span class="hl-string">$1</span>')
               .replace(/:\s*(true|false)/g, ': <span class="hl-boolean">$1</span>')
               .replace(/:\s*(null)/g, ': <span class="hl-null">$1</span>')
               .replace(/:\s*(-?\d+\.?\d*(?:[eE][+-]?\d+)?)/g, ': <span class="hl-number">$1</span>');
  }

  private highlightSearch(html: string, query: string): string {
    const escaped = query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    return html.replace(/(<[^>]*>)|([^<]+)/g, (match, tag, text) => {
      if (tag) return tag;
      return text.replace(new RegExp(escaped, 'gi'), (m: string) => `<mark class="search-hl">${m}</mark>`);
    });
  }

  private findSearchMatches(value: unknown, path: (string | number)[], query: string, out: Set<string>): void {
    if (value === null || value === undefined) return;
    const type = this.getJsonType(value);

    if (type === 'object') {
      for (const [k, v] of Object.entries(value as object)) {
        const childPath = [...path, k];
        if (k.toLowerCase().includes(query)) out.add(this.pathKey(childPath));
        this.findSearchMatches(v, childPath, query, out);
      }
    } else if (type === 'array') {
      (value as unknown[]).forEach((v, i) => this.findSearchMatches(v, [...path, i], query, out));
    } else {
      if (String(value).toLowerCase().includes(query)) out.add(this.pathKey(path));
    }
  }

  private validateSchema(
    data: unknown,
    schema: Record<string, unknown>,
    path: string[],
  ): { path: string; message: string }[] {
    const warnings: { path: string; message: string }[] = [];
    const pathStr = path.length === 0 ? '$' : '$.' + path.join('.');

    const expectedType = schema['type'] as string | undefined;
    if (expectedType) {
      const actualType = this.getSchemaType(data);
      if (actualType !== expectedType) {
        warnings.push({ path: pathStr, message: `Expected "${expectedType}", got "${actualType}"` });
        return warnings;
      }
    }

    const required = schema['required'] as string[] | undefined;
    if (required && typeof data === 'object' && data !== null && !Array.isArray(data)) {
      for (const field of required) {
        if (!(field in (data as Record<string, unknown>))) {
          warnings.push({ path: pathStr + '.' + field, message: 'Required field missing' });
        }
      }
    }

    const properties = schema['properties'] as Record<string, Record<string, unknown>> | undefined;
    if (properties && typeof data === 'object' && data !== null && !Array.isArray(data)) {
      for (const [key, propSchema] of Object.entries(properties)) {
        if (key in (data as Record<string, unknown>)) {
          warnings.push(...this.validateSchema((data as Record<string, unknown>)[key], propSchema, [...path, key]));
        }
      }
    }

    const items = schema['items'] as Record<string, unknown> | undefined;
    if (items && Array.isArray(data)) {
      data.forEach((item, i) => {
        warnings.push(...this.validateSchema(item, items, [...path, `[${i}]`]));
      });
    }

    return warnings;
  }

  private getSchemaType(value: unknown): string {
    if (value === null) return 'null';
    if (Array.isArray(value)) return 'array';
    return typeof value;
  }

  private syncTextToData(): boolean {
    try {
      const parsed = this.jsonEditText.trim() === '' ? null : JSON.parse(this.jsonEditText);
      this.internalData.set(parsed);
      this.parseError.set(null);
      return true;
    } catch {
      this.parseError.set('Invalid JSON — fix it before switching views');
      return false;
    }
  }

  // ── Graph helpers ──────────────────────────────────────────────────────────

  private buildGraphNodes(
    value: unknown,
    key: string,
    id: string,
    path: (string | number)[],
    parentId: string | null,
    out: GraphNode[],
    seen: WeakSet<object>,
  ): void {
    const type = this.getJsonType(value);
    if (type !== 'object' && type !== 'array') return; // primitives are handled as props

    // Circular reference guard
    const obj = value as object;
    if (seen.has(obj)) {
      const CHAR_W_BODY = 6.2;
      const circLabel = '[Circular reference]';
      const rowW = '(ref)'.length * CHAR_W_BODY + 16 + circLabel.length * CHAR_W_BODY;
      const w = Math.min(320, Math.max(140, Math.ceil(rowW + 24)));
      out.push({
        id, key, path: [...path], type: 'object',
        props: [{ key: '(ref)', value: circLabel, type: 'string', path: [...path] }],
        x: 0, y: 0, children: [], parent: parentId, collapsed: false, width: w, height: 40,
      });
      return;
    }
    seen.add(obj);

    const HEADER_H = 24;
    const ROW_H = 16;
    const MIN_W = 140;
    const MAX_W = 320;
    const MAX_VAL_CHARS = 40;
    // Approximate char width for monospace 10px / 11px
    const CHAR_W_BODY = 6.2;   // 10px font (props)
    const CHAR_W_HEADER = 6.8; // 11px font (title)
    const PAD_X = 24;          // left + right padding combined

    const entries = type === 'object'
      ? Object.entries(value as object)
      : (value as unknown[]).map((v, i) => [String(i), v] as [string, unknown]);

    // Split entries into primitive props and container children
    const props: GraphProp[] = [];
    const childEntries: [string, unknown][] = [];

    for (const [k, v] of entries) {
      const vType = this.getJsonType(v);
      const childPath = [...path, type === 'array' ? Number(k) : k];
      if (vType === 'object' || vType === 'array') {
        childEntries.push([k, v]);
      } else {
        const displayVal = this.formatLeafValue(v, vType);
        const truncated = displayVal.length > MAX_VAL_CHARS
          ? displayVal.slice(0, MAX_VAL_CHARS - 1) + '…'
          : displayVal;
        props.push({ key: k, value: truncated, type: vType, path: childPath });
      }
    }

    // Compute width to fit content
    // Header: icon (24px) + title + gap + badge
    const badgeText = String(props.length + childEntries.length);
    const headerW = 24 + key.length * CHAR_W_HEADER + 12 + badgeText.length * CHAR_W_HEADER + 8;
    // Props: key + gap + value
    let maxRowW = 0;
    for (const p of props) {
      const rowW = p.key.length * CHAR_W_BODY + 16 + p.value.length * CHAR_W_BODY;
      if (rowW > maxRowW) maxRowW = rowW;
    }
    const contentW = Math.max(headerW, maxRowW + PAD_X);
    const width = Math.min(MAX_W, Math.max(MIN_W, Math.ceil(contentW)));

    const bodyH = props.length > 0 ? props.length * ROW_H + 8 : 4;
    const height = HEADER_H + bodyH;

    const node: GraphNode = {
      id,
      key,
      path: [...path],
      type: type as 'object' | 'array',
      props,
      x: 0,
      y: 0,
      children: [],
      parent: parentId,
      collapsed: false,
      width,
      height,
    };
    out.push(node);

    for (const [k, v] of childEntries) {
      const childId = id === 'root' ? k : `${id}.${k}`;
      const childPath = [...path, type === 'array' ? Number(k) : k];
      node.children.push(childId);
      this.buildGraphNodes(v, k, childId, childPath, id, out, seen);
    }
  }

  // ── Circular reference helpers ─────────────────────────────────────────────

  /** Returns true if the value object graph contains any circular reference. */
  private hasCircularRef(value: unknown): boolean {
    if (value === null || value === undefined || typeof value !== 'object') return false;
    const seen = new WeakSet<object>();
    const check = (v: unknown): boolean => {
      if (v === null || v === undefined || typeof v !== 'object') return false;
      if (seen.has(v as object)) return true;
      seen.add(v as object);
      const entries = Array.isArray(v) ? v : Object.values(v);
      for (const child of entries) {
        if (check(child)) return true;
      }
      seen.delete(v as object);
      return false;
    };
    return check(value);
  }

  /** JSON.stringify replacer that replaces circular references with "[Circular]". */
  private safeReplacer(): (key: string, value: unknown) => unknown {
    const seen = new WeakSet<object>();
    return (_key: string, value: unknown) => {
      if (typeof value === 'object' && value !== null) {
        if (seen.has(value)) return '[Circular]';
        seen.add(value);
      }
      return value;
    };
  }
}
